import PageSEO from '@/components/PageSEO';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { FileText, Download, Shield, Sparkles, ClipboardList, ScrollText, BookOpen } from 'lucide-react';

export const ResourcesPage = () => {
  // Direct Google Drive file links
  const documents = {
    cleaningCareFramelessShower: '/resources/Cleaning-Care-Frameless-Shower.pdf',
    showerguardCleaning: '/resources/ShowerGuard-Cleaning-Guide.pdf',
    showerguardWarranty: '/resources/ShowerGuard-Warranty.pdf',
    showerguardRegistration: '/resources/ShowerGuard-Registration.pdf',
    termsAndConditions: '/resources/Terms-and-Conditions.pdf',
    warrantyFramelessShower: '/resources/Warranty-Frameless-Shower.pdf',
    warrantyMirror: '/resources/Warranty-Mirror.pdf',
    catalog: '/resources/Comal-Glass-2026-Catalog.pdf',
  };
  
  const categories = [
    {
      title: 'Product Catalogs',
      icon: BookOpen,
      description: 'Browse our product offerings and options.',
      documents: [
        {
          name: 'Product Catalog',
          description: 'View our full range of glass products, hardware options, and finishes.',
          link: documents.catalog,
        },
      ],
    },
    {
      title: 'Care & Cleaning Guides',
      icon: Sparkles,
      description: 'Keep your glass looking beautiful with proper care and maintenance.',
      documents: [
        {
          name: 'Frameless Shower Cleaning Care',
          description: 'How to clean and maintain your frameless shower enclosure.',
          link: documents.cleaningCareFramelessShower,
        },
        {
          name: 'ShowerGuard Cleaning Guide',
          description: 'Specific cleaning instructions for ShowerGuard protected glass.',
          link: documents.showerguardCleaning,
        },
      ],
    },
    {
      title: 'Warranty Information',
      icon: Shield,
      description: 'Review warranty coverage and terms for your glass products.',
      documents: [
        {
          name: 'ShowerGuard Limited Lifetime Warranty',
          description: 'Full warranty terms for ShowerGuard protective coating.',
          link: documents.showerguardWarranty,
        },
        {
          name: 'Frameless Shower Warranty',
          description: 'Warranty coverage for frameless shower glass installations.',
          link: documents.warrantyFramelessShower,
        },
        {
          name: 'Mirror Warranty',
          description: 'Warranty information for custom mirror installations.',
          link: documents.warrantyMirror,
        },
      ],
    },
    {
      title: 'Forms & Registration',
      icon: ClipboardList,
      description: 'Register your products and submit warranty claims.',
      documents: [
        {
          name: 'ShowerGuard Warranty Registration',
          description: 'Register your ShowerGuard glass for warranty coverage.',
          link: documents.showerguardRegistration,
        },
      ],
    },
    {
      title: 'Terms & Policies',
      icon: ScrollText,
      description: 'Review our business terms and policies.',
      documents: [
        {
          name: 'Terms and Conditions',
          description: 'Our standard terms and conditions for services.',
          link: documents.termsAndConditions,
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen">
      <PageSEO title="Resources & Downloads" description="Download our product catalog, cleaning guides, warranties, and care instructions for frameless shower glass, ShowerGuard, and mirrors." path="/resources" />
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/5" />
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6">Resources</Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Documents & <span className="text-gradient-cyan">Resources</span>
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
              Access warranty information, care guides, and important documents for your Comal Glass products.
            </p>
          </div>
        </div>
      </section>

      {/* Documents Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container-custom">
          <div className="space-y-16">
            {categories.map((category, categoryIndex) => {
              const Icon = category.icon;
              return (
                <div key={categoryIndex}>
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl sm:text-3xl font-bold">{category.title}</h2>
                      <p className="text-muted-foreground">{category.description}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {category.documents.map((doc, docIndex) => (
                      <a
                        key={docIndex}
                        href={doc.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group"
                      >
                        <Card className="h-full border-none shadow-md hover:shadow-lg transition-all duration-300 group-hover:border-primary/20">
                          <CardContent className="pt-6">
                            <div className="flex items-start gap-4">
                              <div className="p-3 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                                <FileText className="h-6 w-6 text-primary" />
                              </div>
                              <div className="flex-1">
                                <h3 className="font-semibold mb-1 group-hover:text-primary transition-colors">
                                  {doc.name}
                                </h3>
                                <p className="text-sm text-muted-foreground">
                                  {doc.description}
                                </p>
                              </div>
                              <Download className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                            </div>
                          </CardContent>
                        </Card>
                      </a>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Help Section */}
      <section className="py-20">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Need <span className="text-gradient-cyan">Help?</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Can't find what you're looking for? Our team is here to help with any questions about your glass products.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:8303599925"
                className="inline-flex items-center justify-center px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              >
                Call (830) 359-9925
              </a>
              <a
                href="mailto:orders@comalglass.com"
                className="inline-flex items-center justify-center px-6 py-3 border border-primary text-primary rounded-lg hover:bg-primary/10 transition-colors"
              >
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResourcesPage;
